import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowDownLeft, ArrowUpRight, TrendingUp, RefreshCw } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/common/EmptyState";
import { InlineLoading } from "@/components/common/Loading";
import { Money } from "@/components/common/Money";
import { TransactionRow } from "@/components/wallet/TransactionRow";
import { useAuth } from "@/hooks/useAuth";
import { useTransactionsInfinite, usePendingWithdrawal, usePendingDeposit } from "@/hooks/useTransactions";
import { useEarnings } from "@/hooks/useEarnings";
import { confirmPaydunyaToken } from "@/lib/payments";

export function Wallet() {
  const { profile } = useAuth();
  const tx = useTransactionsInfinite();
  const pendingWd = usePendingWithdrawal();
  const pendingDeposit = usePendingDeposit();
  const earnings = useEarnings();
  const qc = useQueryClient();
  const [checkingDeposit, setCheckingDeposit] = useState(false);
  const transactions = tx.data?.pages.flat() ?? [];

  async function checkPendingDeposit() {
    const token = pendingDeposit.data?.paydunya_token;
    if (!token) return;
    setCheckingDeposit(true);
    try {
      await confirmPaydunyaToken(token);
    } catch {
      // ignoré : on retentera, le bandeau reste affiché si toujours pending
    }
    await pendingDeposit.refetch();
    qc.invalidateQueries({ queryKey: ["profile"] });
    qc.invalidateQueries({ queryKey: ["transactions"] });
    setCheckingDeposit(false);
  }

  return (
    <div className="space-y-4 pt-2 pb-2">
      <header className="flex items-center gap-2">
        <Button asChild variant="ghost" size="icon-sm"><Link to="/"><ArrowLeft className="size-[18px]" /></Link></Button>
        <h1 className="text-base font-semibold tracking-tight flex-1 text-center">Wallet</h1>
        <div className="size-8" />
      </header>

      <section className="rounded-2xl border border-white/[0.06] bg-card p-5 shadow-card">
        <p className="text-2xs uppercase tracking-wider text-muted-foreground font-medium">Solde disponible</p>
        <p className="mt-1 text-[40px] leading-none font-bold num tracking-tight">
          <Money amount={profile?.wallet_balance ?? 0} />
        </p>
        <div className="mt-2 flex items-center gap-1.5 text-xs text-success">
          <TrendingUp className="size-3" strokeWidth={2.5} />
          <span className="font-medium num">+ <Money amount={earnings.total} /> cumul gains</span>
        </div>
        {pendingWd.data && (
          <div className="mt-3 rounded-md border border-warning/30 bg-warning/[0.06] px-3 py-2 text-xs text-warning">
            <Money amount={pendingWd.data.amount} /> en attente de validation
          </div>
        )}
        {pendingDeposit.data && (
          <div className="mt-3 flex items-center justify-between gap-2 rounded-md border border-warning/30 bg-warning/[0.06] px-3 py-2 text-xs text-warning">
            <span>
              Dépôt de <Money amount={pendingDeposit.data.amount} /> en attente de confirmation
            </span>
            <Button
              type="button"
              size="sm"
              variant="outline"
              loading={checkingDeposit}
              onClick={checkPendingDeposit}
            >
              <RefreshCw className="size-3.5" /> Vérifier
            </Button>
          </div>
        )}
        <div className="mt-4 grid grid-cols-2 gap-2">
          <Button asChild size="lg" className="w-full">
            <Link to="/wallet/deposit"><ArrowDownLeft className="size-4" /> Déposer</Link>
          </Button>
          <Button asChild size="lg" variant="secondary" className="w-full">
            <Link to="/wallet/withdraw"><ArrowUpRight className="size-4" /> Retirer</Link>
          </Button>
        </div>
      </section>

      <section className="space-y-2">
        <div className="flex items-center justify-between px-0.5">
          <h2 className="text-sm font-semibold tracking-tight">Transactions</h2>
          <Link to="/history" className="text-2xs font-medium text-muted-foreground hover:text-foreground">
            Tout voir →
          </Link>
        </div>
        {tx.isLoading ? (
          <InlineLoading />
        ) : transactions.length === 0 ? (
          <EmptyState title="Aucune transaction" description="Tes recharges et retraits apparaîtront ici." />
        ) : (
          <div className="rounded-xl border border-white/[0.06] bg-card divide-y divide-white/[0.06] overflow-hidden">
            {transactions.map((t) => <TransactionRow key={t.id} tx={t} />)}
            {tx.hasNextPage && (
              <button
                onClick={() => tx.fetchNextPage()}
                disabled={tx.isFetchingNextPage}
                className="w-full py-3 text-2xs font-medium text-muted-foreground hover:text-foreground hover:bg-white/[0.02] transition-colors"
              >
                {tx.isFetchingNextPage ? "Chargement…" : "Voir plus"}
              </button>
            )}
          </div>
        )}
      </section>
    </div>
  );
}
